/**
 * Which schema the mapping assistant was working against. An analysis pins the schema it was
 * proposed with, so this is the truth about that proposal even if the case's schema changed
 * since. One sentence, and what the schema allows behind a button.
 */

import type { TFunction } from 'i18next'
import { Info } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import type { SchemaInfo } from '@/services/api/structuredIngest'
import { cn } from '@/utils/cn'
import { entityLabelColor } from '@/utils/entity-colors'
import { Fold } from './recipe-cards'

/**
 * Below `md` a popover taller than the room left on screen scrolls as a whole, so the
 * boxes inside it drop their own height caps there: one scroller, not two nested ones.
 */
export const POPOVER_SCROLLS =
  'max-md:max-h-[calc(var(--radix-popover-content-available-height)-0.5rem)] max-md:overflow-y-auto'

function schemaName(info: SchemaInfo, t: TFunction): string {
  if (info.name) return info.name
  return info.source === 'seed'
    ? t('tableMapping.schema.seedName')
    : t('tableMapping.schema.caseName')
}

export function SchemaBand({ info }: { info: SchemaInfo }) {
  const { t } = useTranslation()
  const name = schemaName(info, t)
  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5 text-sm text-muted-foreground">
      <span>
        {t('tableMapping.schema.mapsOnto')}{' '}
        <span className="font-medium text-foreground">{name}</span>
        {info.version && ` (${t('tableMapping.schema.version', { version: info.version })})`}
        {': '}
        {t('tableMapping.schema.counts', {
          entities: t('tableMapping.count.entityTypes', { count: info.labels.length }),
          relationships: t('tableMapping.count.relationshipTypes', {
            count: info.relationshipTypes.length,
          }),
        })}
      </span>
      {info.source !== 'case' && (
        <Badge variant="warning">{t('tableMapping.schema.noCaseSchema')}</Badge>
      )}
      <SchemaDetails info={info} name={name} />
    </div>
  )
}

function SchemaDetails({ info, name }: { info: SchemaInfo; name: string }) {
  const { t } = useTranslation()
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button type="button" size="xs" variant="ghost" className="h-8 px-1.5 text-xs md:h-6">
          <Info aria-hidden className="size-3.5" />
          {t('tableMapping.schema.allows')}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className={cn('w-[28rem] text-xs', POPOVER_SCROLLS)}>
        <div className="flex flex-col gap-3">
          <p className="text-muted-foreground">{t('tableMapping.schema.intro', { name })}</p>
          <div className="flex flex-col gap-1.5">
            <p className="font-medium text-foreground">
              {t('tableMapping.schema.entityTypes', { count: info.labels.length })}
            </p>
            <div className="flex flex-wrap gap-1">
              {info.labels.map(l => (
                <span key={l} className={cn('rounded-full px-2 py-0.5', entityLabelColor(l))}>
                  {l}
                </span>
              ))}
            </div>
          </div>
          <div className="flex flex-col gap-1.5">
            <p className="font-medium text-foreground">
              {t('tableMapping.schema.relationshipTypes', { count: info.relationshipTypes.length })}
            </p>
            <div className="flex flex-wrap gap-1 font-mono">
              {info.relationshipTypes.map(r => (
                <span key={r} className="rounded-full border px-2 py-0.5">
                  {r}
                </span>
              ))}
            </div>
          </div>
          {info.patterns.length > 0 && (
            <Fold title={t('tableMapping.schema.patterns', { count: info.patterns.length })}>
              <ul className="overflow-auto font-mono text-2xs text-muted-foreground md:max-h-56">
                {info.patterns.map(p => (
                  <li key={p.join('|')} className="truncate">
                    {p[0]} <span className="text-foreground">{p[1]}</span> {p[2]}
                  </li>
                ))}
              </ul>
            </Fold>
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}
